import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { format } from 'date-fns';
import { Search, PhoneCall, Phone, Send, StickyNote, RefreshCw } from 'lucide-react';
import { reconnectionLeadAPI, customerAPI } from '../services/api';
import ResponsiveTable from '../components/common/ResponsiveTable';
import LeadNotesModal from '../components/common/LeadNotesModal';
import useDarkReady from '../theme/useDarkReady';

// Inactive CableTV customers (ACTIVE_CATV Box Status STOP / DEACTIVE / SUSPEND — NOC left out)
// for the office to call and bring back. Call notes and follow-up status are kept in the CRM
// (ReconnectionLead); "Send Plans" sends Message Center's "Come Back Offer" template.
const STATUS_LABELS = {
  pending: 'Not called yet', called: 'Called', no_answer: 'No answer', interested: 'Interested',
  not_interested: 'Not interested', wrong_number: 'Wrong number', reconnected: 'Reconnected',
};
const STATUS_COLORS = {
  pending: 'var(--text-muted)', called: 'var(--c-2563eb)', no_answer: '#ea580c', interested: 'var(--c-16a34a)',
  not_interested: 'var(--c-b91c1c)', wrong_number: '#9333ea', reconnected: '#0d9488',
};
const BOX_STATUSES = ['STOP', 'DEACTIVE', 'SUSPEND'];
// What staff can set it to (backend BOX_STATUS_OPTIONS).
const BOX_STATUS_OPTIONS = ['ACTIVE', 'STOP', 'DEACTIVE', 'SUSPEND', 'NOC'];
const PAGE_SIZE = 50;

function SendPlansButton({ lead }) {
  const [sending, setSending] = useState(false);
  if (!lead.customerId || !lead.mobile) return null;
  const send = async () => {
    setSending(true);
    try {
      const { data } = await customerAPI.sendTemplate(lead.customerId, 'win_back');
      toast.success(data.message || 'Sent');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Send failed');
    } finally { setSending(false); }
  };
  return (
    <button className="btn btn-sm" onClick={send} disabled={sending} title="Send plans on WhatsApp (Message Center → Come Back Offer)"
      style={{ background: '#0d9488', borderColor: '#0d9488', color: 'var(--c-ffffff)', padding: '3px 8px', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
      <Send size={13} /> Plans
    </button>
  );
}

export default function ReconnectionLeadsPage() {
  useDarkReady();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ search: '', boxStatus: '', followUpStatus: '', society: '', month: '', from: '', to: '' });
  const [page, setPage] = useState(1);
  const [notesFor, setNotesFor] = useState(null); // accountNumber

  const load = () => {
    setLoading(true);
    reconnectionLeadAPI.getAll()
      .then(({ data }) => setLeads(data.data || []))
      .catch(err => toast.error(err.response?.data?.message || 'Could not load the list'))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const patchLead = (accountNumber, patch) =>
    setLeads(ls => ls.map(l => (l.accountNumber === accountNumber ? { ...l, ...patch } : l)));

  const setStatus = async (lead, status) => {
    try {
      await reconnectionLeadAPI.setStatus(lead.accountNumber, status);
      patchLead(lead.accountNumber, { followUpStatus: status });
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not update status');
    }
  };

  // Writes the sheet. NOC / ACTIVE take the customer off this list, but only on the next refresh,
  // so a slip can still be changed back.
  const setBoxStatus = async (lead, boxStatus) => {
    if (!window.confirm(`Change Box Status of ${lead.name || lead.accountNumber} from ${lead.boxStatus} to ${boxStatus} on the sheet?`)) return;
    try {
      await reconnectionLeadAPI.setBoxStatus(lead.accountNumber, boxStatus);
      patchLead(lead.accountNumber, {
        boxStatus,
        notes: [...lead.notes, { _id: `local-${Date.now()}`, text: `Box Status ${lead.boxStatus} → ${boxStatus}`, createdAt: new Date().toISOString() }],
      });
      toast.success(`Box Status set to ${boxStatus}`);
      if (!BOX_STATUSES.includes(boxStatus)) toast(`${boxStatus} customers leave this list on the next refresh.`);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not change Box Status');
    }
  };

  const societies = useMemo(() => [...new Set(leads.map(l => l.society).filter(Boolean))].sort(), [leads]);
  const counts = useMemo(() => leads.reduce((a, l) => ({ ...a, [l.followUpStatus]: (a[l.followUpStatus] || 0) + 1 }), {}), [leads]);

  // inactiveSince is "YYYY-MM-DD" (or null), so month = its first 7 chars and plain string
  // comparison works for the From/To range.
  const months = useMemo(() => [...new Set(leads.map(l => l.inactiveSince?.slice(0, 7)).filter(Boolean))].sort().reverse(), [leads]);

  const filtered = useMemo(() => {
    const q = filters.search.trim().toLowerCase();
    return leads.filter(l =>
      (!filters.month || (filters.month === 'none' ? !l.inactiveSince : l.inactiveSince?.startsWith(filters.month)))
      && (!filters.from || (l.inactiveSince && l.inactiveSince >= filters.from))
      && (!filters.to || (l.inactiveSince && l.inactiveSince <= filters.to))
      && (!filters.boxStatus || l.boxStatus === filters.boxStatus)
      && (!filters.followUpStatus || l.followUpStatus === filters.followUpStatus)
      && (!filters.society || l.society === filters.society)
      && (!q || [l.name, l.accountNumber, l.mobile, l.society, l.houseNumber].some(v => (v || '').toLowerCase().includes(q))));
  }, [leads, filters]);
  const pages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const shown = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const setFilter = (k, v) => { setFilters(f => ({ ...f, [k]: v })); setPage(1); };

  const columns = [
    {
      key: 'name', label: 'Customer', title: true,
      render: l => (
        <span>
          {l.customerId
            ? <Link to={`/customers/${l.customerId}`} style={{ fontWeight: 600 }}>{l.name || '—'}</Link>
            : <span style={{ fontWeight: 600 }}>{l.name || '—'}</span>}
          <span className="text-muted" style={{ fontSize: '.75rem', marginLeft: 6 }}>{l.accountNumber}</span>
        </span>
      ),
    },
    { key: 'society', label: 'Society / House', render: l => [l.society, l.houseNumber].filter(Boolean).join(' · ') || '—' },
    {
      key: 'mobile', label: 'Mobile', half: true,
      render: l => l.mobile
        ? <a href={`tel:${l.mobile}`} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><Phone size={13} />{l.mobile}</a>
        : '—',
    },
    {
      key: 'boxStatus', label: 'Box Status', half: true,
      render: l => (
        <select className="form-control" value={l.boxStatus} onChange={e => setBoxStatus(l, e.target.value)}
          title="Changes Box Status on the ACTIVE_CATV sheet"
          style={{ padding: '2px 6px', fontSize: '.8rem', fontWeight: 600, minWidth: 100 }}>
          {[...new Set([l.boxStatus, ...BOX_STATUS_OPTIONS])].map(s => <option key={s} value={s}>{s}</option>)}
        </select>
      ),
    },
    { key: 'package', label: 'Last Package', half: true, render: l => l.package || '—' },
    {
      key: 'inactiveSince', label: 'Inactive Since', half: true,
      render: l => (l.inactiveSince ? format(new Date(`${l.inactiveSince}T00:00:00`), 'dd MMM yyyy') : '—'),
    },
    { key: 'reason', label: 'Sheet Reason / Note', render: l => [l.reason, l.sheetNote].filter(Boolean).join(' · ') || '—' },
    {
      key: 'followUp', label: 'Follow-up',
      render: l => (
        <select className="form-control" value={l.followUpStatus} onChange={e => setStatus(l, e.target.value)}
          style={{ padding: '2px 6px', fontSize: '.8rem', color: STATUS_COLORS[l.followUpStatus], fontWeight: 600, minWidth: 130 }}>
          {Object.entries(STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
      ),
    },
    {
      key: 'notes', label: 'Last Note',
      render: l => {
        const last = l.notes[l.notes.length - 1];
        return (
          <button className="btn btn-secondary btn-sm" onClick={() => setNotesFor(l.accountNumber)}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 4, maxWidth: 220, textAlign: 'left' }}
            title={last ? last.text : 'Add a note'}>
            <StickyNote size={13} />
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {last ? last.text : 'Add note'}
            </span>
            {l.notes.length > 1 && <span className="text-muted">({l.notes.length})</span>}
          </button>
        );
      },
    },
    { key: 'send', label: '', render: l => <SendPlansButton lead={l} /> },
  ];

  const notesLead = notesFor && leads.find(l => l.accountNumber === notesFor);

  return (
    <div className="page-content">
      <div className="customers-header mb-4">
        <div>
          <h2 style={{ fontSize: '1.1rem', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8 }}>
            <PhoneCall size={18} /> Reconnection Calls
          </h2>
          <p className="text-muted text-small">
            {leads.length} inactive CableTV customers (STOP / DEACTIVE / SUSPEND on the sheet, NOC excluded)
            {' · '}{counts.pending || 0} not called · {counts.interested || 0} interested · {counts.reconnected || 0} reconnected
          </p>
        </div>
        <button className="btn btn-secondary btn-sm" onClick={load} disabled={loading} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <RefreshCw size={14} /> Refresh
        </button>
      </div>

      <div className="filter-bar customers-filters">
        <div className="search-input-wrapper customers-search">
          <Search size={14} className="search-icon" />
          <input className="search-input" placeholder="Search name, account no., mobile, society..."
            value={filters.search} onChange={e => setFilter('search', e.target.value)} />
        </div>
        <select className="form-control customers-select" value={filters.boxStatus} onChange={e => setFilter('boxStatus', e.target.value)}>
          <option value="">All Box Status</option>
          {BOX_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <select className="form-control customers-select" value={filters.followUpStatus} onChange={e => setFilter('followUpStatus', e.target.value)}>
          <option value="">All Follow-up</option>
          {Object.entries(STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v} ({counts[k] || 0})</option>)}
        </select>
        <select className="form-control customers-select" value={filters.society} onChange={e => setFilter('society', e.target.value)}>
          <option value="">All Societies</option>
          {societies.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <select className="form-control customers-select" value={filters.month} onChange={e => setFilter('month', e.target.value)}
          title="Month the connection went inactive (sheet D.O.E, else the date on the last Update If Any line)">
          <option value="">All Months</option>
          {months.map(m => <option key={m} value={m}>{format(new Date(`${m}-01T00:00:00`), 'MMM yyyy')}</option>)}
          <option value="none">No date</option>
        </select>
        <input type="date" className="form-control customers-select-sm" value={filters.from} onChange={e => setFilter('from', e.target.value)} title="Inactive from" />
        <input type="date" className="form-control customers-select-sm" value={filters.to} onChange={e => setFilter('to', e.target.value)} title="Inactive to" />
        <button className="btn btn-secondary btn-sm"
          onClick={() => { setFilters({ search: '', boxStatus: '', followUpStatus: '', society: '', month: '', from: '', to: '' }); setPage(1); }}>
          Reset
        </button>
      </div>

      <div className="card">
        <div className="table-container">
          {loading ? (
            <div style={{ textAlign: 'center', padding: 40 }}><div className="spinner" style={{ margin: '0 auto' }} /></div>
          ) : shown.length === 0 ? (
            <div className="empty-state">
              <PhoneCall size={32} />
              <h3>No customers found</h3>
              <p>Try changing your filters.</p>
            </div>
          ) : (
            <ResponsiveTable columns={columns} rows={shown} rowKey={l => l.accountNumber} />
          )}
        </div>

        {pages > 1 && (
          <div className="pagination">
            <span style={{ fontSize: '.8rem', color: 'var(--text-muted)', marginRight: 8 }}>
              Page {page} of {pages} · {filtered.length} total
            </span>
            <button className="page-btn" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>←</button>
            <button className="page-btn" disabled={page >= pages} onClick={() => setPage(p => p + 1)}>→</button>
          </div>
        )}
      </div>

      {notesLead && (
        <LeadNotesModal lead={notesLead} onClose={() => setNotesFor(null)}
          onSaved={d => patchLead(notesLead.accountNumber, { notes: d.notes, followUpStatus: d.status, lastCalledAt: d.lastCalledAt })} />
      )}
    </div>
  );
}
